import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try {
        localStorage.setItem("prosperr_user", JSON.stringify(data));
      } catch (err) {
        // localStorage may be full or disabled (private browsing) — non-fatal
        console.warn("[auth] cache user failed:", err.message);
      }
    } catch (err) {
      // Offline? Restore last known user from localStorage so app remains usable.
      if (!navigator.onLine) {
        try {
          const cached = localStorage.getItem("prosperr_user");
          if (cached) { setUser(JSON.parse(cached)); return; }
        } catch (cacheErr) {
          console.warn("[auth] restore cached user failed:", cacheErr.message);
        }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // 2FA challenge: do NOT persist anything, return flags to caller
    if (data.requires_2fa) {
      return { requires_2fa: true, pending_2fa_token: data.pending_2fa_token };
    }
    localStorage.setItem("prosperr_token", data.token);
    setUser(data.user);
    return { user: data.user };
  };

  const verify2faLogin = async (pending_2fa_token, code) => {
    const { data } = await api.post("/auth/2fa/login-verify", { pending_2fa_token, code });
    localStorage.setItem("prosperr_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("prosperr_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network error on logout is fine — we still clear local state
      console.warn("[auth] logout API call failed:", err.message);
    }
    localStorage.removeItem("prosperr_token");
    setUser(null);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh, verify2faLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
